import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, Stethoscope, Search } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";
// Use the logo from public/lovable-uploads
const logoImage = "/images/physiowrld-logo.webp";

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: t('nav.home'), href: "/" },
    { name: t('nav.services'), href: "/services" },
    { name: t('nav.diagnostics'), href: "/dijagnostika" },
    
    { name: t('nav.about'), href: "/about" },
    { name: t('nav.blog'), href: "/blog" },
    { name: t('nav.contact'), href: "/contact" },
    // English-only entry: dedicated English landing hub for expats
    ...(i18n.language === 'en' ? [{ name: 'For expats', href: '/physical-therapy-belgrade' }] : []),
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <nav className="fixed top-0 w-full z-50 glass-card border-b border-border/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src={logoImage} 
              alt="PHYSIOWRLD klinika fizikalne terapije – logo" 
              className="h-10 w-14"
              width="56"
              height="40"
            />
            <span className="text-2xl font-bold gradient-text">PHYSIOWRLD</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  isActive(item.href)
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            <Link to="/pretraga" aria-label="Pretraga" className="text-muted-foreground hover:text-primary transition-colors">
              <Search className="h-5 w-5" />
            </Link>
            <a
              href="tel:+381605037477"
              className="flex items-center space-x-2 text-primary hover:text-primary-glow transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm font-medium">+381 60 503 7477</span>
            </a>
            <Button variant="hero" size="sm" asChild>
              <Link to="/contact">{t('nav.contact')}</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className="h-6 w-6 text-foreground" />
            ) : (
              <Menu className="h-6 w-6 text-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 border-t border-border/20">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 text-base font-medium transition-colors ${
                    isActive(item.href)
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  } rounded-md`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="px-3 py-2 space-y-3">
                <LanguageSwitcher />
                <Link to="/pretraga" onClick={() => setIsOpen(false)} className="flex items-center gap-2 text-foreground hover:text-primary">
                  <Search className="h-4 w-4" /> Pretraga
                </Link>
                <Button variant="outline" size="sm" className="w-full border-primary/40 text-primary hover:bg-primary/10" asChild>
                  <Link to="/sta-me-boli" onClick={() => setIsOpen(false)}>
                    <Stethoscope className="mr-2 h-4 w-4" />
                    {t('nav.symptomAdvisor')}
                  </Link>
                </Button>
                <a
                  href="tel:+381605037477"
                  className="flex items-center space-x-2 text-primary hover:text-primary-glow transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  <span className="text-sm font-medium">+381 60 503 7477</span>
                </a>
                <Button variant="hero" size="sm" className="w-full" asChild>
                  <Link to="/contact" onClick={() => setIsOpen(false)}>
                    {t('nav.contact')}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;